import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ApolloProvider } from '@apollo/client';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import client from './graphql/client';
import { ThemeProvider } from './contexts/ThemeContext';
import { WorkspaceContext, WorkspaceProvider } from './contexts/WorkspaceContext';
import Sidebar from './components/sidebar/Sidebar';
import UnifiedChatPanel from './components/chat/UnifiedChatPanel';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import AuditDashboard from './components/AuditDashboard';
import BookmarksView from './components/BookmarksView';
import CostDashboard from './components/CostDashboard';
import LiveVoicePanel from './components/voice/LiveVoicePanel';
import AdminVoiceScaffold from './components/voice/AdminVoiceScaffold';
import config from './config';
import PrivateRoute from './auth/PrivateRoute';
import './App.css';

export function Dashboard() {
  const SIDEBAR_MIN_WIDTH = 260;
  const SIDEBAR_MAX_WIDTH = 560;
  const SIDEBAR_DEFAULT_WIDTH = 280;

  const { selectedConversation } = useContext(WorkspaceContext);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const appContainerRef = useRef(null);
  const location = useLocation();
  const isUserManagementRoute = location.pathname.startsWith('/admin/users');
  const isActivityRoute =
    location.pathname.startsWith('/activity') || location.pathname.startsWith('/admin/audit');
  const isCostRoute = location.pathname.startsWith('/admin/costs');
  const isVoiceLiveRoute = location.pathname.startsWith('/voice/live');
  const isVoiceAdminRoute = location.pathname.startsWith('/admin/voice');
  const isBookmarksRoute = location.pathname.startsWith('/bookmarks');

  const handleSidebarResizeStart = (event) => {
    if (isSidebarCollapsed) return;
    event.preventDefault();
    setIsResizingSidebar(true);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed((current) => !current);
  };

  const handleResizeMove = useCallback(
    (event) => {
      if (!appContainerRef.current) return;
      const containerRect = appContainerRef.current.getBoundingClientRect();
      const nextWidth = event.clientX - containerRect.left;
      const clampedWidth = Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, nextWidth));
      setSidebarWidth(clampedWidth);
    },
    [SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH]
  );

  useEffect(() => {
    if (!isResizingSidebar) return undefined;

    const handlePointerUp = () => {
      setIsResizingSidebar(false);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handlePointerUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handlePointerUp);
    };
  }, [handleResizeMove, isResizingSidebar]);

  return (
    <div className="app">
      <div
        className={`app-container ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}
        ref={appContainerRef}
      >
        <aside
          id="workspace-sidebar"
          className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}
          style={isSidebarCollapsed ? undefined : { width: `${sidebarWidth}px` }}
          aria-label="Workspace navigation"
        >
          <Sidebar
            onToggleSidebar={toggleSidebar}
            isSidebarCollapsed={isSidebarCollapsed}
          />
        </aside>
        {isSidebarCollapsed && (
          <div className="sidebar-collapsed-rail">
            <button
              type="button"
              className="sidebar-expand-toggle"
              onClick={toggleSidebar}
              aria-controls="workspace-sidebar"
              aria-expanded={!isSidebarCollapsed}
              aria-label="Expand sidebar"
              title="Show sidebar"
            >
              <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path d="M6.646 4.646a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-3 3a.5.5 0 11-.708-.708L9.293 8 6.646 5.354a.5.5 0 010-.708z" />
              </svg>
            </button>
          </div>
        )}
        {!isSidebarCollapsed && (
          <div
            className="sidebar-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            onMouseDown={handleSidebarResizeStart}
          />
        )}
        <main className="main-content">
          {isUserManagementRoute ? (
            <UserManagement />
          ) : isActivityRoute ? (
            <AuditDashboard />
          ) : isBookmarksRoute ? (
            <BookmarksView />
          ) : isCostRoute ? (
            <CostDashboard />
          ) : isVoiceLiveRoute ? (
            config.features?.voiceOpsEnabled ? (
              <LiveVoicePanel />
            ) : (
              <div className="empty-state">
                <p>Voice Ops is disabled in this environment.</p>
              </div>
            )
          ) : isVoiceAdminRoute ? (
            config.features?.voiceOpsEnabled ? (
              <AdminVoiceScaffold />
            ) : (
              <div className="empty-state">
                <p>Voice Ops is disabled in this environment.</p>
              </div>
            )
          ) : selectedConversation ? (
            <UnifiedChatPanel
              key={selectedConversation.id}
              conversation={selectedConversation}
            />
          ) : (
            <div className="empty-state">
              <p>
                Hi there, I'm ready to help. Start a new chat or pick a conversation and
                we'll jump in together.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ApolloProvider client={client}>
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/conversations" replace />} />

            <Route element={<PrivateRoute />}>
              <Route
                path="/conversations"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
              <Route
                path="/conversations/:id"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
              <Route
                path="/activity"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
              <Route path="/admin/audit" element={<Navigate to="/activity" replace />} />
              <Route
                path="/bookmarks"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
              <Route
                path="/admin/costs"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
              <Route
                path="/voice/live"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
              <Route
                path="/admin/voice"
                element={
                  <WorkspaceProvider>
                    <Dashboard />
                  </WorkspaceProvider>
                }
              />
            </Route>
          </Routes>
        </Router>
      </ThemeProvider>
    </ApolloProvider>
  );
}

export default App;
